/**
 * device-score.js — the number /system-diagnostics puts in the ring.
 * `window.DeviceScore`.
 *
 * WHY THIS MODULE EXISTS (user report #1682, 15.09.2026)
 * -----------------------------------------------------
 * Анна ran the tool on four devices and reported: new iPhone Pro Max — 83,
 * six-year-old un-updated Android — 83, a PC that bluescreens daily — 93,
 * the same PC after a factory reset — 88. Her conclusion: "чем меньше — тем
 * лучше". She read her own data correctly; the number was the problem.
 *
 * Three defects, all measured before this module was written:
 *
 * 1. THE NUMBER MOSTLY MEASURED THE BROWSER. The old formula read hardware
 *    through APIs only Chromium ships (deviceMemory, performance.memory,
 *    NetworkInformation) and, when they were absent, handed out PARTIAL credit
 *    inside the FULL maximum — arithmetically a penalty for not being Chrome.
 *    One machine, one tool page, three engines (measured 17.09.2026,
 *    tests/playwright/system-diagnostics-score-is-portable.mjs):
 *    Chromium 79 · Firefox 75 · WebKit 65. On 90 days of prod (8451 scored
 *    sessions, both shards): Chromium desktop p50 84-85, 15% reached the top
 *    grade; of 328 Safari/Firefox sessions **zero ever did** — max 79 on iOS,
 *    82 on Firefox. The top grade was structurally unreachable on those engines.
 *
 *    The old code already carried the right doctrine in its own comment —
 *    "Unmeasured is NOT zero: exclude it from the maximum" — and applied it to
 *    three axes out of nine (battery, storage, benchmark; added 2026-07-03 after
 *    report #910, the same complaint one iteration earlier). The other six kept
 *    paying consolation points. So the rule is enforced HERE, by the evaluator,
 *    once, for every axis: `needs` unsatisfied → the axis leaves the maximum. An
 *    axis cannot opt out, and a new axis cannot forget.
 *
 * 2. THE NUMBER MOVED WHEN THE DEVICE DID NOT. Battery level, disk fullness,
 *    network quality and page-load time were 43 of the 100 points. Measured on
 *    one identical laptop profile: plugged in, fast wifi, empty disk — 92; the
 *    same machine on battery, on hotel wifi, disk 80% full, slow page load — 65.
 *    That is the 93→88 Анна saw. State cannot be compared between two devices
 *    and must not move a device rating, so it is no longer in the number at all:
 *    `kind:'state'` axes produce FINDINGS, which the tool already renders in its
 *    recommendations block, where they are actionable.
 *
 * 3. THE ONE REAL MEASUREMENT WAS OPTIONAL AND ENGINE-BIASED. Only 11.3% of
 *    sessions ever pressed "run benchmark", so for ~9 users in 10 the number
 *    contained no measurement of their machine whatsoever. And the benchmark
 *    itself was not comparable: its Math.sin/cos/sqrt phase runs 3.01× apart
 *    across engines (V8 dropped its sin cache, SpiderMonkey keeps fdlibm), which
 *    made the whole probe 1.71× browser-dependent on one machine. `cpuBenchmark`
 *    below drops that phase for integer / sort / memory-walk / FMA work, all of
 *    which measured 1.31-1.50× apart: cross-engine spread 1.71× → **1.17×**, CV
 *    2.7-4.3%, and still monotone under real core contention (385→316→307→257→88
 *    at 0/8/16/32/48 competing workers). It now runs automatically.
 *
 * WHAT THE NUMBER IS. "How much device is under this browser" — cores, RAM, GPU
 * class, panel, and measured CPU speed. It is NOT a health check: a browser
 * cannot see a bluescreen, a failing disk or a bad driver, and a number that
 * claims to is the reason Анна trusted "93" from a machine she knew was sick.
 * The tool says so in the ring label and in brk_note.
 *
 * RESIDUAL, STATED HONESTLY: the engine is still inside every JS measurement.
 * ~1.17× on CPU, and GPU throughput varies ~8% between Chrome and Firefox on one
 * card. That is well below the 10-20× spread between real devices, but two
 * devices measured in two engines are comparable to roughly ±15%, not exactly.
 *
 * TWO SCALES, ONE DOCTRINE (2026-10-28, report on /ru/phone-benchmark).
 * /phone-benchmark had grown its own copy of the number and every defect this
 * module was written to remove came back in it: a name-table GPU class (every
 * Snapdragon "Entry" — the regex expected digits right after "ADRENO" and the
 * browser says "Adreno (TM) 740"), a CPU probe whose guessed baseline 92% of
 * real phones exceeded, deviceMemory's privacy bucket printed as the RAM size,
 * and network latency plus battery charge inside a DEVICE score. So it now
 * scores HERE, with `compute(input, {scale:'phone'})`. The rules are the same
 * (excluded, not guessed; state is a finding); only the anchors differ, and
 * each set is measured on its own population — a phone and a workstation on
 * one ruler would put every phone in the bottom third, which tells a phone
 * owner nothing they can act on.
 *
 * CONTRACT
 *   DeviceScore.compute(input, {scale}) → {score, max, coverage, parts, findings}
 *       scale: 'device' (default, /system-diagnostics) | 'phone' (/phone-benchmark)
 *   DeviceScore.cpuBenchmark() → Promise<{score, ms}>
 *   DeviceScore.AXES   — the registry itself (walked by the guard test)
 *   DeviceScore.SCALES — the anchors per scale
 *
 * `score` is null when too little could be measured (coverage < MIN_COVERAGE):
 * a ratio over a handful of points is not a rating, and printing one anyway is
 * how "83" came to mean nothing.
 */
(function () {
    'use strict';

    var TOTAL = 100;          /* scoring weights must sum to this */
    var MIN_COVERAGE = 0.45;  /* below this the ratio is not reported */

    /* Reference for cpuBenchmark(): score = REF / elapsed, so a device at the
     * reference speed scores 500 and twice that speed scores 1000. REF carries
     * over the old probe's prod calibration (median 69 ms → 34500/ms) rescaled
     * by the measured Chromium ratio between the two workloads (0.855,
     * interleaved runs on one machine), because Chromium is 96% of the scored
     * population and the old thresholds were calibrated on it. */
    var BENCH_REF = 29500;

    /* The GPU axis divides by the top of GpuDetect's 0-30 tier table, not by 30:
     * nothing in that table actually scores 30 (an RX 9000 is 29), and dividing
     * by an unreachable ceiling is the same saturation bug at the other end of
     * the scale — it makes the top grade impossible for everyone instead of for
     * one browser. */
    var GPU_TOP = 29;

    /* GPU throughput (GpuDetect.bench() "gops") → the same 0-29 class scale the
     * renderer-string table uses, interpolated INSIDE the tier bands instead of
     * snapping to them.
     *
     * Snapping was measured to be its own browser bias: one RTX 4060, quiet page,
     * Chrome 272 gops and Firefox 213 — a genuine 22% engine difference that the
     * 220-gops Ultra cut turned into a whole tier, worth 5 of 100 points. The
     * anchors below are GpuDetect's own tier cut-offs paired with the middle of
     * each tier's score range, so this is a smoothing of that table, not a second
     * opinion about hardware. Interpolation is in log space because the quantity
     * is a throughput. */
    var GOPS_ANCHORS = [[1, 5], [5, 11], [25, 17], [90, 24], [220, 28], [600, 29]];
    function gpuClassFromGops(gops) {
        if (!(gops > 0)) return null;
        var A = GOPS_ANCHORS;
        if (gops <= A[0][0]) return A[0][1];
        for (var i = 1; i < A.length; i++) {
            if (gops <= A[i][0]) {
                var t = Math.log(gops / A[i - 1][0]) / Math.log(A[i][0] / A[i - 1][0]);
                return Math.round((A[i - 1][1] + t * (A[i][1] - A[i - 1][1])) * 10) / 10;
            }
        }
        return A[A.length - 1][1];
    }

    /* ── Calibrations ─────────────────────────────────────────────────
     * 'device' — the anchors /system-diagnostics has always used.
     * 'phone'  — measured on phones only (prod, both shards, 2026-10-28):
     *
     *   cpu: cpuBenchmark() on 142 phone sessions (best run each, 09-18…10-27):
     *        fastest 36 ms · p5 53 · p25 72 · p50 101 · p75 160 · p95 557 ·
     *        p99 1586. The scale is 0 at score 20 (≈1.5 s) and full at 1000
     *        (29.5 ms): 18% above the fastest phone ever seen, so next year's
     *        flagship still has somewhere to go instead of tying today's.
     *   gpu: GpuThroughput units/ms (the /gpu-benchmark shader workload) on
     *        1272 phone runs: p1 85k · p5 153k · p25 370k · p50 709k ·
     *        p75 2.04M · p95 4.75M. 0 at 60k (Mali-T720 / G31 class), full at
     *        6M — only M-series iPads reach it. Per chip (median): PowerVR
     *        GE8320 155k → 21% · Mali-G52 MC2 274k → 33% · Mali-G57 MC2 440k →
     *        43% · Adreno 610 593k → 50% · Adreno 642L 1.07M → 63% · Mali-G615
     *        MC6 1.56M → 71% · Adreno 750 2.75M → 83%.
     *   ram: Chromium on Android reports at most 8 (it rounds to the nearest
     *        power of two and clamps to 1…8 — approximated_device_memory.cc),
     *        so a 12 GB and an 8 GB phone both read 8. 8 is the top of what
     *        the platform can tell and scores full; awarding more for a number
     *        no phone can report would be a ceiling nobody reaches.
     *   display: phone panels, not monitors — FHD+ (2400) is the middle and
     *        QHD+/iPhone Pro (≥2500) the top; a 2556-px iPhone Pro on the
     *        monitor ladder (2560 = top) lost three points for four pixels.
     *
     * Re-fit from phone_bench_complete (cpu_ms, gpu_tp), not by feel. */
    var SCALES = {
        device: {
            cpu: [90, 900],
            gpuTp: null,
            ram: [[32, 16], [16, 14], [8, 11], [4, 7], [2, 4], [0, 1]],
            display: function (d) {
                var p = 0;
                p += d.longPx >= 2560 ? 9 : d.longPx >= 1920 ? 6 : d.longPx >= 1280 ? 3 : 1;
                if (d.dpr >= 2) p += 2;
                if (num(d.refresh) !== null) p += d.refresh >= 110 ? 2 : d.refresh >= 80 ? 1 : 0;
                if (d.wideGamut) p += 1;
                return p;
            }
        },
        phone: {
            cpu: [20, 1000],
            gpuTp: [60000, 6000000],
            ram: [[8, 16], [4, 11], [2, 6], [0, 2]],
            display: function (d) {
                var p = 0;
                p += d.longPx >= 2500 ? 6 : d.longPx >= 2000 ? 4 : d.longPx >= 1500 ? 2 : 1;
                if (num(d.refresh) !== null) p += d.refresh >= 110 ? 4 : d.refresh >= 80 ? 2 : 0;
                if (d.wideGamut) p += 2;
                if (d.hdr) p += 2;
                return p;
            }
        }
    };

    function rungPts(v, ladder) {
        for (var k = 0; k < ladder.length; k++) if (v >= ladder[k][0]) return ladder[k][1];
        return ladder[ladder.length - 1][1];
    }

    function num(v) { return typeof v === 'number' && isFinite(v) ? v : null; }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    /* Points for a value on a log scale: `lo` scores 0, `hi` scores max. */
    function logPts(v, lo, hi, max) {
        if (!(v > 0)) return 0;
        return clamp(Math.round(max * Math.log(v / lo) / Math.log(hi / lo)), 0, max);
    }

    /* ── The registry ────────────────────────────────────────────────────
     * kind: 'device' — a property of the machine; enters the number
     *       'state'  — what is happening right now; NEVER enters the number,
     *                  becomes a finding instead
     *
     * WHAT THE BROWSER CAN DO IS DELIBERATELY NOT IN HERE, even though the page
     * reports it in its own card. A feature list is a property of the software
     * the visitor chose, not of the machine they own, and putting it in a number
     * labelled "device" means telling an iPhone owner their phone is worse
     * because Safari has no WebUSB. Measured on one machine through the real
     * page (17.09.2026), per-axis ratios re-weighted: with a 20-point browser
     * axis the same PC scored 85 in Chrome and 77 in Firefox and the emulated
     * iPhone 64; without it, 84 / 80 / 72. Half the remaining engine spread was
     * that axis alone.
     * needs(i): everything score(i) reads must be covered here. The evaluator
     *           excludes the axis when it returns false and never calls score(). */
    var AXES = [
        {
            key: 'cpu_speed', kind: 'device', weight: 32, tkey: 'brk_cpu_speed',
            needs: function (i) { return num(i.bench) !== null && i.bench > 0; },
            score: function (i, w, sc) { return logPts(i.bench, sc.cpu[0], sc.cpu[1], w); }
        },
        {
            /* The scale stops at 8 because that is where the reporting stops:
             * WebKit caps navigator.hardwareConcurrency at 8, measured on this
             * 32-core machine (Chrome 32 · Firefox 32 · WebKit 8). Awarding more
             * for 9+ cores would be a band only non-Safari users can enter —
             * the same structural ceiling this rewrite exists to remove. Raw
             * power is measured by cpu_speed instead. */
            key: 'cpu_cores', kind: 'device', weight: 10, tkey: 'brk_cpu_cores',
            needs: function (i) { return num(i.cores) !== null && i.cores > 0; },
            score: function (i) { var c = i.cores; return c >= 8 ? 10 : c >= 6 ? 8 : c >= 4 ? 6 : c >= 2 ? 3 : 1; }
        },
        {
            /* Chromium-only (deviceMemory). Excluded — not guessed — elsewhere.
             * Note the ceiling is 32, not 8: Chromium stopped clamping the
             * reported value, and prod confirms it (16 GB and 32 GB both occur;
             * of 563 Edge desktop sessions 54% report more than 8). */
            key: 'memory', kind: 'device', weight: 16, tkey: 'brk_memory',
            needs: function (i) { return num(i.ram) !== null && i.ram > 0; },
            score: function (i, w, sc) { return rungPts(i.ram, sc.ram); }
        },
        {
            /* Class of the graphics stack on the 0-30 scale produced by the
             * shared GpuDetect etalon — parsed from a trustworthy renderer
             * string, or MEASURED by its throughput probe when the string is
             * masked or bucketed. When neither is trustworthy (iOS masks every
             * GPU as "Apple GPU", and its throughput probe is not reliable — the
             * same exclusion /gaming-pc-scanner already makes) the axis is
             * excluded rather than guessed.
             *
             * On the phone scale the input is `gpuTp` — measured throughput
             * (GpuThroughput) and nothing else: a phone GPU is never ranked by
             * its name, not even as a fallback. */
            key: 'gpu', kind: 'device', weight: 28, tkey: 'brk_gpu',
            needs: function (i, sc) {
                if (sc.gpuTp) return num(i.gpuTp) !== null && i.gpuTp > 0;
                return !!i.gpuClass && num(i.gpuClass.score) !== null && i.gpuClass.score > 0;
            },
            score: function (i, w, sc) {
                if (sc.gpuTp) return logPts(i.gpuTp, sc.gpuTp[0], sc.gpuTp[1], w);
                return clamp(Math.round(i.gpuClass.score / GPU_TOP * w), 0, w);
            }
        },
        {
            /* The panel is the one piece of hardware every engine reports the
             * same way, refresh rate included (measured over a second of rAF). */
            key: 'display', kind: 'device', weight: 14, tkey: 'brk_display',
            needs: function (i) { return !!i.display && num(i.display.longPx) !== null && i.display.longPx > 0; },
            score: function (i, w, sc) { return clamp(sc.display(i.display), 0, w); }
        },
        /* ── state: findings only, never points ─────────────────────────── */
        {
            key: 'battery', kind: 'state', tkey: 'brk_battery',
            needs: function (i) { return !!i.state && !!i.state.battery; },
            finding: function (i) {
                var b = i.state.battery;
                if (b.charging) return null;
                if (b.level < 20) return { level: 'bad', key: 'battery_low' };
                if (b.level < 50) return { level: 'warn', key: 'battery_mid' };
                return null;
            }
        },
        {
            /* The DISK first. storageFreeBytes is the room the browser really
               grants (window.__storageEstimate): on a nearly full system disk
               Chromium grants a site nothing, while estimate() still reports
               usage + 10 GiB — so the percentage below read «1 % used, plenty of
               room» on exactly the machines whose exports then failed (support
               chat 647). The percentage stays for browsers that report a real
               quota (Safari & co.), where a site near its own share is its own
               problem. */
            key: 'storage', kind: 'state', tkey: 'brk_storage',
            needs: function (i) { return !!i.state && (num(i.state.storageFreeBytes) !== null || num(i.state.storagePct) !== null); },
            finding: function (i) {
                var free = num(i.state.storageFreeBytes);
                if (free !== null && free < 500 * 1048576) return { level: 'bad', key: 'disk_full' };
                if (free !== null && free < 2048 * 1048576) return { level: 'warn', key: 'disk_low' };
                var p = num(i.state.storagePct);
                if (p !== null && p > 90) return { level: 'bad', key: 'storage_full' };
                if (p !== null && p > 75) return { level: 'warn', key: 'storage_mid' };
                return null;
            }
        },
        {
            key: 'network', kind: 'state', tkey: 'brk_network',
            needs: function (i) { return !!i.state && !!i.state.net; },
            finding: function (i) {
                var n = i.state.net;
                if (!n.online) return { level: 'bad', key: 'net_offline' };
                if (n.type === '2g' || n.type === 'slow-2g') return { level: 'warn', key: 'net_slow' };
                if (num(n.rtt) !== null && n.rtt > 300) return { level: 'warn', key: 'net_latency' };
                return null;
            }
        },
        {
            /* A machine whose GPU driver is missing renders through SwiftShader
             * or llvmpipe. That is a real, fixable condition of the moment — and
             * a plausible reason a freshly reset PC scored LOWER than the sick
             * one it replaced — so it is said out loud instead of silently
             * subtracting points. */
            key: 'gpu_software', kind: 'state', tkey: 'brk_gpu_software',
            needs: function (i) { return typeof i.gpuSoftware === 'boolean'; },
            finding: function (i) { return i.gpuSoftware ? { level: 'warn', key: 'gpu_software' } : null; }
        },
        {
            key: 'heap', kind: 'state', tkey: 'brk_heap',
            needs: function (i) { return !!i.state && num(i.state.heapPct) !== null; },
            finding: function (i) { return i.state.heapPct > 80 ? { level: 'warn', key: 'heap_high' } : null; }
        }
    ];

    function compute(input, opts) {
        var i = input || {};
        var sc = SCALES[(opts && opts.scale) || 'device'] || SCALES.device;
        var raw = 0, max = 0, parts = [], findings = [];

        for (var k = 0; k < AXES.length; k++) {
            var ax = AXES[k];
            var have = false;
            try { have = !!ax.needs(i, sc); } catch (_) { have = false; }

            if (ax.kind === 'state') {
                if (!have) continue;
                var f = null;
                try { f = ax.finding(i); } catch (_) { f = null; }
                if (f) findings.push({ key: f.key, level: f.level, axis: ax.key });
                continue;
            }

            if (!have) {
                parts.push({ key: ax.key, tkey: ax.tkey, kind: ax.kind, excluded: true, max: ax.weight });
                continue;
            }
            var pts = clamp(Math.round(ax.score(i, ax.weight, sc)), 0, ax.weight);
            raw += pts; max += ax.weight;
            parts.push({ key: ax.key, tkey: ax.tkey, kind: ax.kind, pts: pts, max: ax.weight });
        }

        var coverage = max / TOTAL;
        return {
            score: coverage >= MIN_COVERAGE ? clamp(Math.round(raw / max * 100), 0, 100) : null,
            raw: raw, max: max, coverage: coverage, parts: parts, findings: findings
        };
    }

    /* ── CPU probe ───────────────────────────────────────────────────────
     * Integer LCG · Float64 sort · strided memory walk · FMA accumulation.
     * No Math.sin/cos: that phase alone measured 3.01× apart across engines and
     * dominated the old total. Runs in a worker, never on the main thread.
     * `ph` — the four phases' own times inside the same run (/cpu-benchmark
     * shows them per row); stamping them adds four clock reads, the total
     * `ms` every scale is calibrated on is what it always was. */
    var BENCH_BODY =
        'self.onmessage=function(){' +
        'var t0=performance.now(),x=1;' +
        'for(var i=0;i<4000000;i++){x=((x*1103515245+12345)&0x7fffffff)^(x>>>16);x=(x+(x<<3))|0}' +
        'var t1=performance.now();' +
        'var a=new Float64Array(120000);for(var i=0;i<a.length;i++){x=(x*1103515245+12345)&0x7fffffff;a[i]=x/2147483647}a.sort();' +
        'var t2=performance.now();' +
        'var buf=new Uint8Array(8<<20);for(var k=0;k<6;k++)for(var i=0;i<buf.length;i+=64)buf[i]=(buf[i]+i+k)&255;' +
        'var m=0;for(var i=0;i<buf.length;i+=4096)m+=buf[i];' +
        'var t3=performance.now();' +
        'var f=new Float64Array(1<<16);for(var i=0;i<f.length;i++)f[i]=i*0.5;' +
        'var acc=0;for(var r=0;r<60;r++)for(var i=0;i<f.length;i++)acc+=f[i]*1.0000001;' +
        'var t4=performance.now();' +
        'self.postMessage({ms:t4-t0,ph:[t1-t0,t2-t1,t3-t2,t4-t3],preventOpt:x+a[0]+m+acc})};';

    /* Work done by each phase above, for "operations per second" per row:
       LCG steps · Float64 elements generated and sorted · 64-byte strides
       written (6 passes over 8 MiB) · multiply-adds (60 × 65 536). */
    var PHASE_UNITS = [4000000, 120000, 6 * ((8 << 20) / 64), 60 * 65536];

    function cpuBenchmark() {
        return new Promise(function (resolve, reject) {
            var url = null, w = null, done = false, timer = null;
            function finish(fn, arg) {
                if (done) return;
                done = true;
                if (timer) clearTimeout(timer);
                try { if (w) w.terminate(); } catch (_) {}
                try { if (url) URL.revokeObjectURL(url); } catch (_) {}
                fn(arg);
            }
            try {
                url = URL.createObjectURL(new Blob([BENCH_BODY], { type: 'application/javascript' }));
                w = new Worker(url);
            } catch (e) { finish(reject, e); return; }
            /* Devices slow enough to blow this deadline exist (the old probe's
               worst prod sample was 18.5 s); the axis is then excluded rather
               than left pending forever. */
            timer = setTimeout(function () { finish(reject, new Error('bench-timeout')); }, 30000);
            w.onerror = function (e) { finish(reject, e); };
            w.onmessage = function (e) {
                var ms = Math.max((e.data && e.data.ms) || 0, 1);
                finish(resolve, { score: clamp(Math.round(BENCH_REF / ms), 1, 2000), ms: Math.round(ms),
                                  phases: (e.data && e.data.ph) || null });
            };
            try { w.postMessage(null); } catch (e) { finish(reject, e); }
        });
    }

    window.DeviceScore = {
        AXES: AXES,
        SCALES: SCALES,
        TOTAL: TOTAL,
        MIN_COVERAGE: MIN_COVERAGE,
        BENCH_REF: BENCH_REF,
        PHASE_UNITS: PHASE_UNITS,
        compute: compute,
        gpuClassFromGops: gpuClassFromGops,
        cpuBenchmark: cpuBenchmark
    };
})();
